import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const refreshToken = async () => {
  try {
    const currentToken = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/api/auth/refresh-token`, {}, {
      headers: { 'Authorization': `Bearer ${currentToken}` }
    });
    const { access_token } = response.data;
    setAuthToken(access_token);
    return access_token;
  } catch (error) {
    console.error('خطا در تجدید توکن:', error);
    throw error;
  }
};

let isRefreshing = false;
let refreshSubscribers = [];

const processQueue = (error, token = null) => {
  refreshSubscribers.forEach(cb => cb(error, token));
  refreshSubscribers = [];
};

export const setupTokenRefresh = () => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        try {
          const token = await new Promise((resolve, reject) => {
            refreshSubscribers.push((error, token) => {
              if (error) reject(error);
              else resolve(token);
            });
          });
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_URL}/api/auth/refresh-token`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        setAuthToken(access_token);
        
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        processQueue(null, access_token);
        
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000;
    return Date.now() < expiry;
  } catch {
    return false;
  }
};

export const login = async (username, password) => {
  try {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      username,
      password
    });
    return response.data;
  } catch (error) {
    console.error('Login error:', error);
    if (error.response) {
      throw new Error(error.response.data.detail || 'خطا در ورود');
    } else if (error.request) {
      throw new Error('سرور پاسخگو نیست. لطفاً بعداً تلاش کنید.');
    } else {
      throw new Error('خطا در ارسال درخواست');
    }
  }
};

export const register = async (username, email, password) => {
  try {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      username,
      email,
      password
    });
    console.log('Register response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Register error:', error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const validateToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;

    const response = await axios.get(
      `${API_URL}/api/auth/validate-token`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.valid;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

export const loginWithGoogle = async (credential) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/auth/google-login`,
      { credential }
    );
    
    const { access_token, user } = response.data;
    setAuthToken(access_token);
    return { token: access_token, user };
  } catch (error) {
    console.error('Google login error:', error);
    throw new Error(error.response?.data?.detail || 'خطا در ورود با گوگل');
  }
};
